import { titleize } from 'inflected';
import { currencyFormatter, numberFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { formatPercent as bpmFormatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import {
  MODEL_TYPE_LABELS,
  MODEL_TYPE_ORDER,
  ModelType,
  ModelTypeType,
  PRODUCT_TYPE_TO_MODEL_TYPE,
  ProductType,
  ProductTypeType,
  SECTION_ORDER,
  SectionKeyType,
  SectionStatus,
  SectionStatusType,
  SUMMARY_SECTIONS
} from './data-qa-constants';
import { CampaignLabeling, ConfigSummary, WeekRow } from './data-qa-types';

// The Campaigns stepper pill is derived from the labeling state, not approval:
// blocking present → 'blocking'; only proactive → 'labelSoon'; clean → 'labeled'.
// Returns undefined when validation didn't run (no pill shown).
export type CampaignPillType = 'labeled' | 'labelSoon' | 'blocking';
export const campaignPillStatus = (
  labeling: CampaignLabeling | undefined
): CampaignPillType | undefined => {
  if (!labeling || labeling.availability !== 'ready') return undefined;
  if (labeling.blocking.length > 0) return 'blocking';
  if (labeling.proactive.length > 0) return 'labelSoon';
  return 'labeled';
};

export const formatCurrency = (value: number) => currencyFormatter.format(value);

export const formatNumber = (value: number) => numberFormatter.format(value);

export const formatPercent = (value: number, fractionDigits = 1) =>
  bpmFormatPercent(value, fractionDigits);

export const formatRelativeTime = (iso: string) => {
  const date = new Date(iso);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 60) return '1 month ago';
  return `${Math.floor(days / 30)} months ago`;
};

export const buildEmptyStatusRecord = (): Record<SectionKeyType, SectionStatusType> => {
  const record = {} as Record<SectionKeyType, SectionStatusType>;
  for (const key of SECTION_ORDER) record[key] = SectionStatus.UNREVIEWED;
  return record;
};

export const buildEmptyNotesRecord = (): Record<SectionKeyType, string> => {
  const record = {} as Record<SectionKeyType, string>;
  for (const key of SECTION_ORDER) record[key] = '';
  return record;
};

// SUMMARY_SECTIONS, not SECTION_ORDER -- Campaigns and Feature Mapping (the other two
// entries in SECTION_ORDER) have no approve/flag UI at all (see SUMMARY_SECTIONS' own
// doc comment) and sit permanently at their buildEmptyStatusRecord default of
// UNREVIEWED. Counting them here would report a misleading "X of 6" instead of "X of
// 4", and iterating SECTION_ORDER in allSectionsActioned/allSectionsApproved below
// would make both permanently unsatisfiable -- statusRecord[CAMPAIGNS] can never
// become anything other than UNREVIEWED, so `.every(...)` could never pass.
export const countByStatus = (statusRecord: Record<SectionKeyType, SectionStatusType>) => {
  let approved = 0;
  let flagged = 0;
  let unreviewed = 0;
  for (const key of SUMMARY_SECTIONS) {
    if (statusRecord[key] === SectionStatus.APPROVED) {
      approved += 1;
    } else if (statusRecord[key] === SectionStatus.FLAGGED) {
      flagged += 1;
    } else {
      unreviewed += 1;
    }
  }
  return { approved, flagged, unreviewed, total: SUMMARY_SECTIONS.length };
};

export const allSectionsActioned = (statusRecord: Record<SectionKeyType, SectionStatusType>) =>
  SUMMARY_SECTIONS.every(key => statusRecord[key] !== SectionStatus.UNREVIEWED);

export const allSectionsApproved = (statusRecord: Record<SectionKeyType, SectionStatusType>) =>
  SUMMARY_SECTIONS.every(key => statusRecord[key] === SectionStatus.APPROVED);

// Oxford-comma join for a list of model labels in a sentence, e.g. "Commerce MMM",
// "Commerce MMM and Brand Equity", or "Commerce MMM, Brand Equity, and Geo MMM".
export const formatModelNameList = (labels: string[]): string => {
  if (labels.length <= 1) return labels[0] ?? '';
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
};

// Canonical model-type identity for a config, derived from its product type.
export const modelTypeForProductType = (productType: ProductTypeType): ModelTypeType =>
  PRODUCT_TYPE_TO_MODEL_TYPE[productType] ?? ModelType.DTC;

// One reviewable model, which may be backed by more than one config. Brand Equity
// is always exactly two configs (two modeling stages, the second consuming the
// first's output as a context var) that review as a single model — every other
// product type is a singleton group.
export interface ModelGroup {
  key: string;
  productType: ProductTypeType;
  // The model type, plus the model's KPI when the client has more than one of that type.
  label: string;
  configs: ConfigSummary[];
}

// A backend without modelKey: Brand Equity groups per client, every other config is its own model.
const modelKeyOf = (config: ConfigSummary): string =>
  config.modelKey ??
  (config.productType === ProductType.BRAND_EQUITY
    ? `${config.clientName}|${config.productType}|${config.regionalScope}`
    : config.configPath);

// "custom__gmv_dollars" -> "Gmv Dollars"
const kpiLabel = (config: ConfigSummary): string =>
  titleize(
    (config.dependentVarLabel ?? config.dependentVar ?? '').replace(/^(kpi|custom|combo)__/, '')
  );

// Group configs by modelKey into one model each, then order the groups in canonical
// model-type order (DTC -> Commerce -> geoMMM -> Brand Equity) and each group's configs
// by brandEquityStage ('s1' before 's2') so the lower-numbered stage is always the
// group's default/primary config.
export const groupConfigsByModel = (configs: ConfigSummary[]): ModelGroup[] => {
  const groups = new Map<string, ModelGroup>();
  for (const config of configs) {
    const key = modelKeyOf(config);
    const existing = groups.get(key);
    if (existing) {
      existing.configs.push(config);
    } else {
      groups.set(key, { key, productType: config.productType, label: '', configs: [config] });
    }
  }
  for (const group of groups.values()) {
    // configPath as the tiebreaker (not just brandEquityStage) so which config
    // represents a stage is deterministic rather than depending on S3 listing order.
    group.configs.sort((a, b) => {
      const stageCompare = (a.brandEquityStage ?? '').localeCompare(b.brandEquityStage ?? '');
      return stageCompare !== 0 ? stageCompare : a.configPath.localeCompare(b.configPath);
    });
    // Brand Equity's YouGov BrandIndex stage can be backed by MORE than one config file
    // sharing the SAME brandEquityStage (one per metric -- awareness/consideration/
    // buzz/purchase_intent -- see bpm-lambdas' YOUGOV_BRAND_INDEX_METRICS), but every
    // consumer of this group (DataQA.tsx's configs[0]/configs[1] for primary/secondary
    // stage, DependentVarStageTabs) still assumes exactly one config per stage, per this
    // function's own "always exactly two configs" doc comment above -- confirmed as a
    // real bug against eos (2026-08-29): configs[1] resolved to a second YouGov metric
    // config instead of the real Total Business (s2) config, so both stage tabs loaded
    // a YouGov config and both showed "BrandIndex". Collapsed here to the first
    // (alphabetically, for determinism) config per distinct stage value -- lossless,
    // since any one of the same-stage sibling configs loads the identical combined
    // dataset (getMMMDataQALoad reads all 4 metrics off the same shared national
    // dataframe regardless of which one is "active").
    const seenStages = new Set<string>();
    group.configs = group.configs.filter(config => {
      const stage = config.brandEquityStage ?? '';
      if (seenStages.has(stage)) return false;
      seenStages.add(stage);
      return true;
    });
  }
  const typeCounts = new Map<ModelTypeType, number>();
  for (const group of groups.values()) {
    const modelType = modelTypeForProductType(group.productType);
    typeCounts.set(modelType, (typeCounts.get(modelType) ?? 0) + 1);
  }
  for (const group of groups.values()) {
    const modelType = modelTypeForProductType(group.productType);
    const typeLabel = MODEL_TYPE_LABELS[modelType];
    // The last config is Brand Equity's stage 2, whose KPI names the model.
    const kpi = kpiLabel(group.configs[group.configs.length - 1]);
    group.label = (typeCounts.get(modelType) ?? 0) > 1 && kpi ? `${typeLabel} · ${kpi}` : typeLabel;
  }
  return [...groups.values()].sort(
    (a, b) =>
      MODEL_TYPE_ORDER.indexOf(modelTypeForProductType(a.productType)) -
        MODEL_TYPE_ORDER.indexOf(modelTypeForProductType(b.productType)) ||
      a.label.localeCompare(b.label)
  );
};

// The display label of the model a config belongs to (see ModelGroup.label).
export const modelLabelForConfig = (configs: ConfigSummary[], configPath: string): string => {
  const config = configs.find(c => c.configPath === configPath);
  if (!config) return '';
  const key = modelKeyOf(config);
  return groupConfigsByModel(configs).find(g => g.key === key)?.label ?? '';
};

// Keeps the current selection while it is still one of `configs`; otherwise (first load, or a
// company switch) falls back to the first model's primary config.
export const resolveSelectedConfigPath = (
  configs: ConfigSummary[],
  current: string | undefined
): string | undefined =>
  configs.some(c => c.configPath === current)
    ? current
    : groupConfigsByModel(configs)[0]?.configs[0]?.configPath;

// Last calendar day of a bucketWeeksByGranularity bucket key (weekIso) -- weekly
// buckets are Monday-anchored, so their last day is 6 days later; monthly buckets are
// the 1st of the month, so their last day is that month's last day. Daily/unrecognized
// granularity has no coarser bucket, so the bucket IS the single day (end === start).
const bucketEndDate = (weekIso: string, reviewGranularity: string | undefined): string => {
  const granularity = (reviewGranularity ?? '').trim().toLowerCase();
  const d = new Date(`${weekIso}T00:00:00Z`);
  // A malformed weekIso yields an Invalid Date here -- .toISOString() below throws a
  // RangeError on one of those, which would propagate out of filterWeeksToFencepost
  // during render and blank the whole page. Falling back to weekIso itself keeps the
  // "malformed input passes through gracefully" promise this function's own callers
  // (filterWeeksToFencepost) rely on.
  if (Number.isNaN(d.getTime())) {
    return weekIso;
  }
  if (granularity === 'weekly') {
    d.setUTCDate(d.getUTCDate() + 6);
  } else if (granularity === 'monthly') {
    d.setUTCMonth(d.getUTCMonth() + 1);
    d.setUTCDate(d.getUTCDate() - 1);
  }
  return d.toISOString().slice(0, 10);
};

// Bounds the Portfolio/Channels chart x-axis to the model's own fencepost window
// instead of plotting its entire data history -- `weeks` otherwise spans every date in
// the model's dataframe, which for a client with years of history dwarfs the window the
// model was actually trained/reviewed on. Passes weeks through unchanged when either
// bound is missing (an older config without fencepost dates, or a malformed one) rather
// than silently emptying the chart.
//
// The end bound checks the bucket's LAST day (bucketEndDate), not its first (weekIso) --
// a bucket that starts at or before fencepostEnd but ends after it is a PARTIAL bucket:
// the model's own dataframe usually extends past fencepostEnd, so its dependentValue/
// spend sum for that bucket is a full bucket's worth, while any ground-truth query
// bounded by `date <= fencepost_end_date` (GQV/BPM/YouGov) only contributes the
// in-window days -- comparing a full MMM bucket against a partial canonical one reads as
// a spurious divergence on the model's last reviewed bucket (confirmed against real
// YouGov BrandIndex data, 2026-08-29: the week of 2026-03-30 diverged only because of
// this). Dropping the bucket outright is what "only counting full buckets contained
// within the window" requires -- reviewGranularity comes from dataset, not config,
// since it's the model's declared review cadence, not a fencepost field.
export const filterWeeksToFencepost = <T extends Pick<WeekRow, 'weekIso'>>(
  weeks: T[],
  fencepostStart: string | undefined,
  fencepostEnd: string | undefined,
  reviewGranularity?: string
): T[] => {
  if (!fencepostStart || !fencepostEnd) return weeks;
  return weeks.filter(
    w => w.weekIso >= fencepostStart && bucketEndDate(w.weekIso, reviewGranularity) <= fencepostEnd
  );
};
